import { useRender } from '@base-ui/react/use-render'
import { cn } from '../../lib/cn'
import s from './TextLink.module.css'

/**
 * Texte actionnable — la référence d'une ligne de tableau du prototype : au repos, rien
 * d'un bouton (ni cadre, ni fond, ni retrait) ; l'affordance apparaît au survol, couleur
 * de marque et soulignement.
 *
 * Un contrôle et non une décoration d'écran : il porte survol, désactivation et — par
 * l'anneau global de `base.css`, qu'il ne neutralise pas — le focus clavier. Ces trois
 * états sont ce qui se recopiait d'un module d'écran à l'autre.
 *
 * `<button>` par défaut : le squelette ouvre ses écrans par une prop de rappel, pas par
 * une URL. La prop `render` (idiome Base UI) l'habille sur un autre élément le jour où
 * c'est un vrai lien :
 *
 * ```tsx
 * <TextLink render={<Link to="/inspections/$id" params={{ id }} />}>{reference}</TextLink>
 * ```
 *
 * ⚠️ La désactivation se lit sur l'attribut natif `disabled` : elle n'a de sens que sur un
 * `<button>`. Un `render` vers un `<a>` n'est pas désactivable — c'est le DOM qui le dit,
 * pas nous.
 */
export type TextLinkProps = useRender.ComponentProps<'button'>

export function TextLink({ render, className, ...props }: TextLinkProps) {
  return useRender({
    render,
    defaultTagName: 'button',
    props: {
      className: cn(s.root, className),
      // Même défaut que `Button` et `IconButton` : hors d'un `render`, jamais `submit`
      // implicite.
      ...(render === undefined && { type: 'button' }),
      ...props,
    },
  })
}
